import React from 'react';
import { TruncatedCopy } from 'common/Text/TruncatedCopy';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { canDelete, canUpdate } from 'utils/permissions';
import styled from 'styled-components';
import { DataTableRowAction, DataTableRowActions, DataTableTypes } from '@utmost.co/utmost-design-system';
import { tokens } from '@beeline.com/bp-design-system';
import { useStaticPermission } from '../../../../../common/auth/Authorize';
import { JobFamily } from './jobFamily.graphql';
import { useJobFamiliesIntl } from './translations/useJobFamiliesIntl';

export function useJobFamilyCells(
  onDeleteAll: (uids: string[]) => void,
  setSelectedJobFamily: (resource: JobFamily) => void
) {
  const intl = useJobFamiliesIntl();
  const { extendJobFamilyGroup } = useDarkly();
  const familyPermissions = useStaticPermission('JOB_FAMILY', true);

  const columns: (DataTableTypes.Column<JobFamily & { '[menu]': string }> & { hide?: boolean })[] = [
    {
      accessor: 'name',
      Header: intl.name,
      Cell: ({ value }) => <span data-test="family-name">{value}</span>
    },
    {
      accessor: 'referenceId',
      Header: intl.referenceId,
      ellipsisOnOverflow: false,
      Cell: ({ value }) => (
        <StyledReferenceId data-test="family-ref-id">
          <TruncatedCopy textCopy={value ?? '-'} />
        </StyledReferenceId>
      )
    },
    {
      accessor: 'jobFamilyGroup',
      Header: intl.jobFamilyGroup,
      Cell: ({ value }) => <span data-test="job-family-group">{value?.name ?? '-'}</span>
    },
    {
      accessor: 'summary',
      Header: intl.summary,
      Cell: ({ value }) => <span data-test="job-family-summary">{value ?? '-'}</span>
    },
    {
      hide: !extendJobFamilyGroup || (!familyPermissions.UPDATE && !familyPermissions.DELETE),
      accessor: '[menu]',
      Header: '',
      sticky: 'right',
      Cell({ row }) {
        const updateFlag = canUpdate(row.original.permissions?.JOB_FAMILY) && (
          <DataTableRowAction dataTest="edit-family-mi" onClick={() => setSelectedJobFamily(row.original)}>
            {intl.edit}
          </DataTableRowAction>
        );
        const deleteFlag = canDelete(row.original.permissions?.JOB_FAMILY) && (
          <DataTableRowAction
            style={{ color: tokens.colorAlert }}
            dataTest="delete-family-mi"
            onClick={() => onDeleteAll([row.original.uid])}
          >
            {intl.delete}
          </DataTableRowAction>
        );
        return (
          <DataTableRowActions disabled={!(updateFlag || deleteFlag)}>
            {updateFlag}
            {deleteFlag}
          </DataTableRowActions>
        );
      }
    }
  ];

  return columns.filter((column) => !column.hide);
}

const StyledReferenceId = styled.span`
  width: 100%;
`;
